import { DatePipe } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import moment from 'moment';
import { finalize } from 'rxjs';
import { MaterialModule } from 'src/app/material.module';
import { LicenseService } from 'src/app/services/license.service';
import { ToastService } from 'src/app/services/toast.service';
import { IConfirmDialog } from 'src/app/shared/interfaces/IDialog';
import { ILicense, LicenseStatus } from 'src/app/shared/interfaces/ILicense';
import { getLicenseDaysRemaining, getLicenseStatus } from 'src/app/shared/utils';
import { LicenseStatusComponent } from 'src/app/shared/components/license-status/license-status.component';

const CARD_CLASSES: Partial<Record<LicenseStatus, string>> = {
  active: 'border-emerald-100 border-l-emerald-700 bg-emerald-50/50 dark:bg-emerald-950/30 dark:border-emerald-900 dark:border-l-emerald-500',
  expiringSoon: 'border-amber-200 border-l-amber-600 bg-amber-50/50 dark:bg-amber-950/30 dark:border-amber-900 dark:border-l-amber-500'
};

@Component({
  selector: 'app-user-license',
  imports: [MaterialModule, TranslateModule, DatePipe, LicenseStatusComponent],
  templateUrl: './user-license.component.html',
  host: { class: 'contents' }
})
export class UserLicenseComponent implements OnInit {
  license: ILicense | null = null;
  cancelling = false;

  constructor(
    private toastService: ToastService,
    private translateService: TranslateService,
    private licenseService: LicenseService) { }

  ngOnInit(): void {
    this.loadLicense();
  }

  get status(): LicenseStatus | null {
    return this.license ? getLicenseStatus(this.license) : null;
  }

  get daysRemaining(): number {
    if (!this.license) return 0;
    return getLicenseDaysRemaining(this.license.endDate);
  }

  get cardClasses(): string {
    return CARD_CLASSES[this.status ?? 'active'] ?? CARD_CLASSES.active!;
  }

  get endDateLong(): string {
    if (!this.license) return '';
    const [year, month, day] = this.license.endDate.split('-').map(Number);
    const lang = this.translateService.currentLang || this.translateService.getDefaultLang() || 'en';
    return new Intl.DateTimeFormat(lang, { dateStyle: 'long' }).format(new Date(year, month - 1, day));
  }

  loadLicense() {
    this.licenseService.getMyActiveLicense()
      .subscribe((license) => this.license = license);
  }

  openCancelLicense() {
    const config: IConfirmDialog = {
      title: 'cancel license title',
      content: this.translateService.instant('cancel license content', { date: moment(this.license!.endDate).format('DD.MM.YYYY') }),
      confirmColor: '#dc2626',
      onConfirm: () => this.cancelLicense()
    };
    this.toastService.confirmDelete(config);
  }

  cancelLicense() {
    this.cancelling = true;
    const endDate = moment(this.license!.endDate).format('DD.MM.YYYY');
    this.licenseService.cancelMyLicense().pipe(finalize(() => this.cancelling = false)).subscribe(() => {
      this.toastService.success(this.translateService.instant('license cancelled message', { date: endDate }));
      this.loadLicense();
    });
  }
}
